// convex/lib/config/domains/documents.ts

import { validatorAttachment } from "../../../companies/attachments";
import { validatorTag, validatorEntityTag } from "../../../companies/tags";
import { validatorDocumentTemplate } from "../../../companies/documents/templates";
import { validatorDocumentRecord } from "../../../companies/documents/records";
import { validatorUsers } from "../../../system/users";
import { templateFiltersRule } from "../../../companies/documents/templates/filter_rules";
import type { DomainGroup, TableScope } from "../types";

// ============================================================================
// DOCUMENTS DOMAIN - Status Values for i18n
// ============================================================================

export const DOCUMENT_RECORD_STATUSES = [
  "draft",
  "completed",
  "archived",
] as const;

export const documentsDomain: DomainGroup = {
  id: "documents",
  editions: ["saas", "desktop"],
  name: "Documentos",
  description: "Gestión de documentos, plantillas y archivos adjuntos",
  icon: "FileText",
  order: 9,
  status: "active",

  // ── Configuración i18n del dominio ────────────────────
  i18n: {
    namespace: "documents",
    keys: {
      title: "documents.title",
      description: "documents.description",
    },
  },

  modules: [
    {
      id: "document_templates",
      name: "Plantillas",
      description: "Gestión de plantillas para documentos personalizados",
      icon: "FileStack",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/documents/templates"],
      navigation: {
        showInNav: false,
        contexts: [],
        navOrder: 22,
        status: "active",
      },

      // ── Configuración i18n del módulo ────────────────────
      i18n: {
        namespace: "documents.templates",
        keys: {
          title: "documents.templates.title",
          description: "documents.templates.description",
          longDescription: "documents.templates.longDescription",
          searchPlaceholder: "documents.templates.searchPlaceholder",
          emptyState: "documents.templates.emptyState",
          actions: {
            create: "documents.templates.actions.create",
            edit: "documents.templates.actions.edit",
            delete: "documents.templates.actions.delete",
            view: "documents.templates.actions.view",
          },
        },
        translatableFields: ["name", "description", "content"],
        translatableEnums: [
          {
            field: "categories",
            values: [
              "maintenance",
              "installation",
              "inspection",
              "report",
              "checklist",
              "credential",
              "warranty",
              "contract",
              "custom",
            ],
          },
        ],
      },

      tables: [
        {
          tableName: "document_templates",
          schema: validatorDocumentTemplate,
          scope: "branch" as TableScope,
          relations: [],
          searchFields: ["name", "description", "code"],
          hasStatusIndex: false,
          customRules: [templateFiltersRule],
          searchIndex: {
            name: "search_templates",
            field: "name",
          },
        },
      ],
      readOnlyTables: [],
    },
    {
      id: "document_records",
      name: "Documentos",
      description: "Historial y gestión de documentos generados",
      icon: "FileCheck",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/documents"],
      navigation: {
        showInNav: true,
        contexts: ["main"],
        navOrder: 21,
        status: "active",
      },

      // ── Configuración i18n del módulo ────────────────────
      i18n: {
        namespace: "documents.records",
        keys: {
          title: "documents.records.title",
          description: "documents.records.description",
          longDescription: "documents.records.longDescription",
          searchPlaceholder: "documents.records.searchPlaceholder",
          emptyState: "documents.records.emptyState",
          actions: {
            create: "documents.records.actions.create",
            edit: "documents.records.actions.edit",
            delete: "documents.records.actions.delete",
            view: "documents.records.actions.view",
          },
        },
        translatableFields: ["title", "notes"],
        translatableEnums: [
          { field: "status", values: DOCUMENT_RECORD_STATUSES },
        ],
      },

      tables: [
        {
          tableName: "document_records",
          schema: validatorDocumentRecord,
          scope: "branch" as TableScope,
          relations: [
            {
              key: "template",
              sourceField: "templateId",
              schema: validatorDocumentTemplate,
              tableName: "document_templates",
            },
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "lastCreatedBy",
              sourceField: "internalControl.createdBy",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: ["title", "notes", "data"],
          hasStatusIndex: true,
          customRules: [],
          searchIndex: {
            name: "search_document_records",
            field: "title",
          },
        },
      ],
      readOnlyTables: [{ tableName: "document_templates" }],
    },
    {
      id: "attachments",
      name: "Archivos Adjuntos",
      description: "Gestión de archivos e imágenes adjuntas a entidades",
      icon: "Paperclip",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: [],
      navigation: {
        showInNav: false,
        contexts: [],
        navOrder: 22,
        status: "active",
      },

      // ── Configuración i18n del módulo ────────────────────
      i18n: {
        namespace: "documents.attachments",
        keys: {
          title: "documents.attachments.title",
          description: "documents.attachments.description",
          longDescription: "documents.attachments.longDescription",
          searchPlaceholder: "documents.attachments.searchPlaceholder",
          emptyState: "documents.attachments.emptyState",
          actions: {
            create: "documents.attachments.actions.create",
            delete: "documents.attachments.actions.delete",
            view: "documents.attachments.actions.view",
          },
        },
        translatableFields: [],
        translatableEnums: [],
      },

      tables: [
        {
          tableName: "attachments",
          schema: validatorAttachment,
          scope: "branch" as TableScope,
          relations: [],
          searchFields: ["name", "description"],
          hasStatusIndex: false,
          customRules: [],
          searchIndex: {
            name: "search_attachments",
            field: "name",
          },
        },
      ],
      readOnlyTables: [{ tableName: "clients" }],
    },
    {
      id: "tags",
      name: "Etiquetas",
      description: "Sistema de etiquetas global para búsqueda y organización",
      icon: "Tag",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: [],
      navigation: {
        showInNav: false,
        contexts: [],
        navOrder: 23,
        status: "active",
      },

      // ── Configuración i18n del módulo ────────────────────
      i18n: {
        namespace: "documents.tags",
        keys: {
          title: "documents.tags.title",
          description: "documents.tags.description",
          longDescription: "documents.tags.longDescription",
          searchPlaceholder: "documents.tags.searchPlaceholder",
          emptyState: "documents.tags.emptyState",
          actions: {
            create: "documents.tags.actions.create",
            edit: "documents.tags.actions.edit",
            delete: "documents.tags.actions.delete",
            view: "documents.tags.actions.view",
          },
        },
        translatableFields: ["name"],
        translatableEnums: [],
      },

      tables: [
        {
          tableName: "tags",
          schema: validatorTag,
          scope: "branch" as TableScope,
          relations: [],
          searchFields: ["name", "slug"],
          hasStatusIndex: false,
          customRules: [],
          searchIndex: {
            name: "search_tags",
            field: "name",
          },
        },
        {
          tableName: "entity_tags",
          schema: validatorEntityTag,
          scope: "branch" as TableScope,
          relations: [
            {
              key: "tag",
              sourceField: "tagId",
              schema: validatorTag,
              tableName: "tags",
            },
          ],
          searchFields: [],
          hasStatusIndex: false,
          customRules: [],
        },
      ],
      readOnlyTables: [],
    },
  ],
};
